import { Button, Flex, Group, Skeleton, Stack, Text, Title } from "@mantine/core";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ApiErrorAlert } from "../components/ApiErrorAlert.js";
import { LeadPicker } from "../components/weekly/LeadPicker.js";
import { WeeklyHistoryList } from "../components/weekly/WeeklyHistoryList.js";
import { WeeklyOverview, type WeeklyPaar } from "../components/weekly/WeeklyOverview.js";
import { WeeklyView } from "../components/weekly/WeeklyView.js";
import { useCurrentUser } from "../hooks/useAuth.js";
import {
  useCreateWeekly,
  useWeekly,
  useWeeklyHistory,
  useWeeklyLeads,
  useWeeklyOverview,
} from "../hooks/useWeekly.js";
import { einstieg, hatBereichssicht, weiterleitungsZiel } from "../lib/weekly.js";

export interface WeeklySearch {
  employeeId?: string;
  segmentId?: string;
  weeklyId?: string;
}

const UUID = /^[0-9a-f-]{36}$/i;

/** Lead, Bereich und Weekly stehen in der Adresse — teilbar, und streng geprüft (Adresszeile ist Nutzereingabe). */
export function validateWeeklySearch(search: Record<string, unknown>): WeeklySearch {
  const pick = (k: string) => {
    const v = search[k];
    return typeof v === "string" && UUID.test(v) ? v : undefined;
  };
  return { employeeId: pick("employeeId"), segmentId: pick("segmentId"), weeklyId: pick("weeklyId") };
}

/**
 * Einstieg auf `/weekly`:
 * - mit `employeeId` → das Weekly des Paares (mit Bereichssicht zusätzlich „Zur Übersicht"),
 * - ohne `employeeId`, mit Bereichssicht (BL/Stellv./GL/Admin) → Übersicht,
 * - ohne `employeeId` sonst → direkt das eigene Lead × Segment.
 */
export function WeeklyPage() {
  const search = useSearch({ strict: false }) as WeeklySearch;
  const { user, isLoading } = useCurrentUser();
  const bereichssicht = hatBereichssicht(user);

  // Entscheidung siehe `einstieg` (lib/weekly.ts, dort getestet).
  switch (einstieg(user, isLoading, search)) {
    case "laden":
      return <Skeleton h={300} />;
    case "uebersicht":
      return <WeeklyOverviewPage employeeId={user?.employeeId} />;
    case "lead":
      return <WeeklyLeadPage search={search} bereichssicht={bereichssicht} />;
  }
}

function WeeklyOverviewPage({ employeeId }: { employeeId: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const overview = useWeeklyOverview();
  const oeffne = (paar: WeeklyPaar) => void navigate({ to: "/weekly", search: paar });
  // Umschalter für BL, die selbst Lead sind: erstes eigenes Paar (Sortierung wie die Tabelle).
  const eigen = employeeId ? overview.data?.find((r) => r.employeeId === employeeId) : undefined;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={1}>
          {t("weekly.title")} – {t("weekly.overview.title")}
        </Title>
        {eigen && (
          <Button variant="light" onClick={() => oeffne({ employeeId: eigen.employeeId, segmentId: eigen.segmentId })}>
            {t("weekly.overview.myWeekly")}
          </Button>
        )}
      </Group>
      {overview.error && <ApiErrorAlert error={overview.error} />}
      {overview.isLoading && <Skeleton h={300} />}
      {overview.data && <WeeklyOverview rows={overview.data} onOpen={oeffne} />}
    </Stack>
  );
}

function WeeklyLeadPage({ search, bereichssicht }: { search: WeeklySearch; bereichssicht: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const leads = useWeeklyLeads();

  // Ohne Bereichssicht und ohne Auswahl: das eigene Lead × Segment, sonst der erste Eintrag.
  useEffect(() => {
    const ziel = weiterleitungsZiel({ employeeId: search.employeeId }, leads.data, user?.employeeId);
    if (ziel) void navigate({ to: "/weekly", search: ziel, replace: true });
  }, [leads.data, search.employeeId, user?.employeeId, navigate]);

  const history = useWeeklyHistory(search.employeeId, search.segmentId);
  const create = useCreateWeekly(search.employeeId ?? "", search.segmentId ?? "");

  // Ohne Auswahl: das offene, sonst das jüngste Weekly.
  const gewaehlt =
    search.weeklyId ?? history.data?.items.find((w) => w.status === "offen")?.id ?? history.data?.items[0]?.id;
  const detail = useWeekly(gewaehlt);

  const waehle = (weeklyId: string) =>
    void navigate({ to: "/weekly", search: { employeeId: search.employeeId, segmentId: search.segmentId, weeklyId } });

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={1}>{t("weekly.title")}</Title>
        {bereichssicht && (
          <Button variant="subtle" onClick={() => void navigate({ to: "/weekly", search: {} })}>
            {t("weekly.overview.toOverview")}
          </Button>
        )}
      </Group>
      {leads.error && <ApiErrorAlert error={leads.error} />}
      {leads.data && leads.data.length === 0 && <Text c="dimmed">{t("weekly.noLeads")}</Text>}
      {leads.data && leads.data.length > 1 && (
        <LeadPicker
          leads={leads.data}
          value={
            search.employeeId && search.segmentId
              ? { employeeId: search.employeeId, segmentId: search.segmentId }
              : null
          }
          onChange={(v) => void navigate({ to: "/weekly", search: v })}
        />
      )}
      {history.error && <ApiErrorAlert error={history.error} />}
      {/* Schmal: Historie über dem Weekly; ab sm nebeneinander. */}
      <Flex direction={{ base: "column", sm: "row" }} align={{ base: "stretch", sm: "flex-start" }} gap="xl">
        {history.data && (
          <WeeklyHistoryList
            history={history.data}
            selectedId={gewaehlt}
            creating={create.isPending}
            onSelect={waehle}
            onCreate={() => create.mutate(undefined, { onSuccess: (d) => waehle(d.weekly.id) })}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {detail.isLoading && <Skeleton h={300} />}
          {detail.error && <ApiErrorAlert error={detail.error} />}
          {create.error && <ApiErrorAlert error={create.error} />}
          {detail.data && <WeeklyView key={detail.data.weekly.id} detail={detail.data} />}
        </div>
      </Flex>
    </Stack>
  );
}
